import { Component, OnDestroy, OnInit } from '@angular/core';
import { NotificationsService } from 'app/shared/services-api-central/notifications/notifications.service';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationItem } from 'app/shared/services-api-central/notifications/notifications.interface';
import { ReleaseNotesService } from 'app/shared/services-api-central/release-notes/release-notes.service';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';

@Component({
    selector: 'app-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    animations: [
        trigger('notificationAnimation', [
            state('visible', style({ opacity: 1, transform: 'translateY(0)' })),
            state('hidden', style({ opacity: 0, transform: 'translateY(-20px)' })),
            transition('visible => hidden', [animate('50ms ease-out')]),
            transition('hidden => visible', [animate('200ms ease-in')]),
        ]),
    ],
    standalone: false
})
export class NotificationComponent implements OnInit, OnDestroy {
  public notificationList: NotificationItem[] = [];
  public unreadNotifications: NotificationItem[] = [];
  public selectedNotification: NotificationItem | null = null;
  public animationState: 'visible' | 'hidden' = 'visible';
  public currentUnreadIndex = 0;
  public theme$: Observable<EThemeName>;

  private unsubscriber = new ReplaySubject();

  constructor(
    private notificationsService: NotificationsService,
    private releaseNotesService: ReleaseNotesService,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.theme$ = this.themeService.getTheme();

    this.notificationsService.notifications$.pipe(takeUntil(this.unsubscriber)).subscribe((notifications) => {
      this.notificationList = notifications;
      this.unreadNotifications = this.notificationList?.filter((n) => !n.isRead || n.dismissible === false);

      if (this.unreadNotifications?.length) {
        this.selectUnreadNotification();
      } else {
        this.selectedNotification = null;
      }
    });

  }

  public onAnimationDone(event: any) {
    if (event.toState === 'hidden') {
      this.animationState = 'visible';
      this.selectUnreadNotification();
    }
  }

  private selectUnreadNotification(): void {
    if (!this.unreadNotifications.length) {
      this.selectedNotification = null;
      return;
    }

    if (this.currentUnreadIndex < 0) {
      this.currentUnreadIndex = 0;
    }
    if (this.currentUnreadIndex >= this.unreadNotifications.length) {
      this.currentUnreadIndex = this.unreadNotifications.length - 1;
    }

    this.selectedNotification = this.unreadNotifications[this.currentUnreadIndex];
    this.animationState = 'visible';
  }

  public markAsRead(): void {
    if (this.selectedNotification) {
      this.notificationsService.markAsRead(this.selectedNotification._id);
      this.animationState = 'hidden';
    }
  }

  public previousNotification(): void {
    if (this.currentUnreadIndex > 0) {
      this.currentUnreadIndex--;
      this.selectedNotification = this.unreadNotifications[this.currentUnreadIndex];
      this.animationState = 'visible';
    }
  }

  public nextNotification(): void {
    if (this.currentUnreadIndex < this.unreadNotifications.length - 1) {
      this.currentUnreadIndex++;
      this.selectedNotification = this.unreadNotifications[this.currentUnreadIndex];
      this.animationState = 'visible';
    }
  }

  public openReleaseNotes(): void {
    this.releaseNotesService.open();
  }

  public ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }
}